/**
 * Surface → host → href resolution (doc 32).
 *
 * HOST MODEL. Menuraq is one Next.js app served from several hostnames; the map in
 * `lib/routing/hosts.ts` says which. Each host's ROOT is rewritten by `proxy.ts`
 * onto that host's `mountPath` in `app/`, so `manage.menuraq.com/menus` renders
 * `app/dashboard/menus` while the URL bar shows the short path.
 *
 * TWO ADDRESSING MODES COEXIST, PERMANENTLY (doc 32 §9):
 *
 *   host-based   manage.menuraq.com/menus       ← canonical in production
 *   path-based   localhost:3000/dashboard/menus ← local dev + Vercel previews
 *
 * Path-based is not legacy. Vercel previews are a single hostname with no
 * subdomains, so a preview can only ever address surfaces by path. Every href
 * helper here therefore emits a path-only href by default and only goes absolute
 * when the caller asks — and even then, only when the target host is actually
 * `live` in the map.
 *
 * THAT LAST RULE IS THE CUTOVER SWITCH. A `planned` host resolves to the current
 * origin, so links keep working path-based until its DNS lands; flipping `status`
 * to `'live'` in `hosts.ts` is what moves traffic. Doc 32 D7 forbids an env flag
 * for this, and the reason is recorded in lib/site-url.ts: an unset
 * `NEXT_PUBLIC_CHOW_MIGRATION` once left the entire consumer surface on a retired
 * host while Chow's own domain served the marketing site. A routing switch must
 * not have an "off" state that silently serves the wrong thing — so the switch is
 * a committed line of code, reviewed alongside the DNS change, not an env var.
 */

import { adminEquivalentPath, isBusinessAdminPath } from '@/lib/routing/business-admin';

import {
  PLATFORM_HOSTS,
  DASHBOARD_ROUTE_HOSTS,
  hostDefinition,
  hostOrigin,
  platformHostForHostname,
  type PlatformHost,
} from '@/lib/routing/hosts';

/**
 * @deprecated Use `PlatformHost` from lib/routing/hosts.ts. Kept so existing
 * imports keep compiling; `main` was the old name for `www`.
 */
export type PlatformSurface = PlatformHost | 'main';

/** Hosts that serve page routes (i.e. everything you can build an href to). */
export type PlatformPageSurface = Exclude<PlatformHost, 'api'>;

export function normalizePlatformPath(path: string): string {
  if (!path) return '/';
  return path.startsWith('/') ? path : `/${path}`;
}

/**
 * Local development hostnames.
 *
 * `*.localhost` matters: Chrome and Safari resolve it to loopback with no
 * /etc/hosts edit, so `pos.localhost:3000` exercises the real host router and the
 * real cross-host session hand-off locally, over HTTP. That is a concrete
 * advantage of per-host sessions (doc 32 D4) over a shared `.menuraq.com` cookie,
 * which cannot be tested locally at all without a fake TLD.
 */
export function isLocalPlatformHostname(hostname: string): boolean {
  const normalized = hostname.split(':')[0].toLowerCase();
  return (
    normalized === 'localhost' ||
    normalized === '127.0.0.1' ||
    normalized === '::1' ||
    normalized.endsWith('.localhost')
  );
}

/**
 * The `app/` path prefix a host's routes live under. `''` for www.
 *
 * Note `dashboard` and `manage` BOTH return '/dashboard': they share one mount and
 * differ only by audience (doc 32 §4.2).
 */
export function getPlatformRoutePrefix(surface: PlatformPageSurface): string {
  return hostDefinition(surface).mountPath;
}

/**
 * Which host a hostname belongs to, or `null` for anything unrecognised.
 *
 * Previously stubbed to `return null` when subdomain routing was removed. `null`
 * is still meaningful and still load-bearing — it means "unknown host, serve
 * path-based and change nothing", which is what keeps previews, LAN access and
 * tunnels working (proxy.ts encodes the same decision and warns against
 * "cleaning it up").
 */
export function getPlatformSurfaceForHostname(hostname: string): PlatformHost | null {
  if (!hostname || isLocalPlatformHostname(hostname)) return null;
  return platformHostForHostname(hostname);
}

/** True when the host serves under its own hostname today. */
export function isHostLive(surface: PlatformHost): boolean {
  return hostDefinition(surface).status === 'live';
}

/**
 * The configured origin, as a SINGLE valid origin.
 *
 * `NEXT_PUBLIC_APP_URL` is a comma-separated LIST by design — local development is
 * routinely reached from localhost, a LAN IP and an ngrok tunnel at once, and two
 * other consumers already parse it that way (`getAllowedOrigins` in
 * lib/server/csrf.ts, `resolveConfiguredOrigins` in lib/server/webauthn-core.ts).
 *
 * This function did not, and returned the raw string. Every server-side caller
 * then reached `new URL(path, origin)` in `buildPlatformUrl` with something like
 * `http://localhost:3001,http://172.20.10.8:3001,https://….ngrok-free.dev` and got
 * `TypeError: Invalid URL`.
 *
 * The visible symptom was the hand-off LANDING returning a bare 500 with an empty
 * body — the route's own denial page could not be rendered, because building its
 * "Sign in" href threw. So the one screen whose entire job is to explain a failed
 * sign-in was the screen that could not be shown, and the cross-host hand-off was
 * untestable anywhere but production.
 *
 * Latent in production only because `NEXT_PUBLIC_SITE_URL` is set there and wins
 * the `||`. That is luck, not design: a single stray comma in it would do this
 * everywhere.
 *
 * Takes the first entry that actually parses, so a list, a single value and a
 * value with stray brackets all resolve to something `new URL()` accepts.
 */
function envOrigin(): string {
  const raw = process.env.NEXT_PUBLIC_SITE_URL || process.env.NEXT_PUBLIC_APP_URL || '';
  if (!raw) return '';

  for (const candidate of raw.split(',')) {
    const trimmed = candidate.trim().replace(/^\[|\]$/g, '');
    if (!trimmed) continue;
    try {
      return new URL(trimmed).origin;
    } catch {
      // Try the next entry rather than giving up: a malformed first value must
      // not discard the valid ones behind it.
    }
  }

  // Nothing parsed. Empty string, never the raw value — callers treat '' as "not
  // configured" and fall back to the host map, whereas the raw value would be
  // carried into `new URL()` and throw exactly as before.
  return '';
}

/** The origin we are currently being served from, if we can tell. */
function currentOriginOf(options: { currentOrigin?: string | URL | null }): string | null {
  if (typeof window !== 'undefined') return window.location.origin;
  if (options.currentOrigin) return String(options.currentOrigin);
  return null;
}

/**
 * Which platform host we are currently being served from, or `null` when that is
 * not a production host (localhost, preview, LAN IP, tunnel) or is unknown.
 *
 * This is the guard that keeps host-based addressing from leaking into
 * environments that cannot serve it. Without it, a QR code generated on localhost
 * would encode a production URL, and a preview deployment would emit links to
 * hosts it is not.
 */
export function currentPlatformHost(
  options: { currentOrigin?: string | URL | null } = {},
): PlatformHost | null {
  const current = currentOriginOf(options);
  if (!current) return null;
  try {
    return platformHostForHostname(new URL(current).hostname);
  } catch {
    return null;
  }
}

/**
 * The origin to use for a host.
 *
 * Strictly preserves today's behaviour: the mapped origin is used only when we can
 * see that we are already on a production platform host. Anywhere else — dev,
 * preview, LAN, tunnel — the current origin wins, so nothing that embeds a URL
 * (QR codes, receipts, canonicals) starts pointing at production from a laptop.
 */
export function getPlatformOrigin(
  surface: PlatformSurface,
  options: { currentOrigin?: string | URL | null } = {},
): string {
  const host: PlatformHost = surface === 'main' ? 'www' : surface;
  const current = currentOriginOf(options);

  // The API is never addressed cross-origin (D5).
  if (host === 'api') return current || envOrigin();

  // Server render with no request origin: the env var is the historical answer and
  // in production it agrees with the map.
  if (!current) return envOrigin() || hostOrigin(host);

  // Not on a production host → stay where we are.
  if (!currentPlatformHost(options)) return current;

  return isHostLive(host) ? hostOrigin(host) : current;
}

export function buildPlatformUrl(
  surface: PlatformSurface,
  path: string,
  options: { currentOrigin?: string | URL | null } = {},
): string {
  const origin = getPlatformOrigin(surface, options);
  return new URL(normalizePlatformPath(path), origin).toString();
}

// ─── API paths (doc 32 D5: /api stays same-origin on every host) ─────────────

export function normalizeApiPath(path: string): string {
  const normalized = normalizePlatformPath(path);
  if (normalized === '/api' || normalized.startsWith('/api/')) return normalized;
  if (normalized === '/v1') return '/api/v1';
  if (normalized.startsWith('/v1/')) return `/api${normalized}`;
  return `/api${normalized}`;
}

/**
 * API URLs stay on the CURRENT origin, always.
 *
 * `api.menuraq.com` exists in the host map for machine clients only. First-party
 * calls must not cross an origin: 691 `/api` literals across 227 files, and
 * off-origin calls would force `SameSite=None` cookies (undoing the isolation the
 * per-host session model buys) and downgrade the origin-based CSRF check in
 * proxy.ts from "same origin or reject" to "on a list". That check is the app's
 * primary mutation defence — see doc 32 §7.
 */
export function buildApiUrl(
  path: string,
  options: { currentOrigin?: string | URL | null } = {},
): string {
  return buildPlatformUrl('api', normalizeApiPath(path), options);
}

// ─── Href builders ───────────────────────────────────────────────────────────

/**
 * An href to `path` on `surface`.
 *
 * Callers pass the path WITHOUT the mount prefix, but including it is tolerated and
 * stripped — ~560 call sites were converted from raw literals, and silently
 * accepting `/dashboard/menus` is cheaper than getting it wrong.
 *
 * Three outcomes, in order:
 *
 * 1. **Already on the target host** → a short relative href with NO mount prefix
 *    (`/menus`), because that host's root already IS the mount.
 * 2. **On a different production host, target is live** → an absolute URL, also
 *    without the mount prefix. Emitting `manage.menuraq.com/dashboard/menus` would
 *    rewrite to `/dashboard/dashboard/menus` and 404.
 * 3. **Anything else** (dev, preview, target not live yet) → the path-based form
 *    `${mountPath}${path}`.
 *
 * Case 3 is the safe default and it is always correct: `proxy.ts` passes a path
 * through unrewritten when its first segment is one the host already owns, so
 * `/dashboard/menus` resolves on `manage.menuraq.com` just as well as on
 * localhost. Host-based addressing is the shorter URL, never the only one.
 */
export function buildSurfaceHref(
  surface: PlatformPageSurface,
  path: string,
  options: { currentOrigin?: string | URL | null; absolute?: boolean } = {},
): string {
  const prefix = hostDefinition(surface).mountPath;
  const normalized = normalizePlatformPath(path);

  const cleanPath =
    prefix && normalized === prefix
      ? '/'
      : prefix && normalized.startsWith(`${prefix}/`)
      ? normalized.slice(prefix.length) || '/'
      : normalized;

  const here = currentPlatformHost(options);

  // 1. Same host — emit the short form.
  if (here === surface) {
    return options.absolute ? new URL(cleanPath, hostOrigin(surface)).toString() : cleanPath;
  }

  // 2. Cross-host, and both ends are real. Absolute regardless of `absolute`,
  //    because a relative href cannot leave the host it is clicked on.
  if (here && isHostLive(surface)) {
    return new URL(cleanPath, hostOrigin(surface)).toString();
  }

  // 3. Path-based — always works, on every host and in every environment.
  const fullPath = cleanPath === '/' ? prefix || '/' : `${prefix}${cleanPath}`;
  return options.absolute ? buildPlatformUrl(surface, fullPath, options) : fullPath;
}

type HrefOptions = { currentOrigin?: string | URL | null; absolute?: boolean };

/** The light owner account: read the data, read the audits, basic setup. */
export function businessAdminHref(path = '/', options: HrefOptions = {}) {
  const here = currentPlatformHost(options);
  // Before DNS cutover, retain the full mount on dashboard. Its short /admin
  // address is intentionally reserved/blocked for the platform-console boundary.
  if (!isHostLive('admin') && here && here !== 'admin') {
    const prefix = hostDefinition('admin').mountPath;
    const normalized = normalizePlatformPath(path);
    const mounted = normalized === prefix || normalized.startsWith(`${prefix}/`) ? normalized : `${prefix}${normalized === '/' ? '' : normalized}`;
    return here === 'dashboard' ? mounted : new URL(mounted, hostOrigin('dashboard')).toString();
  }
  return buildSurfaceHref('admin', path, options);
}

/** Explicit exit from Business Admin; never remapped into its own navigation. */
export function workspaceHref(path = '/', options: HrefOptions = {}) {
  return buildSurfaceHref('dashboard', path, options);
}

export function dashboardHref(path = '/', options: HrefOptions = {}) {
  const normalized = normalizePlatformPath(path);
  const mounted = normalized.startsWith('/dashboard') ? normalized : `/dashboard${normalized === '/' ? '' : normalized}`;
  if (isBusinessAdminPath(mounted)) return businessAdminHref(mounted, options);
  const here = currentPlatformHost(options);
  const inAdmin = here === 'admin' || (typeof window !== 'undefined' && isBusinessAdminPath(window.location.pathname));
  const equivalent = inAdmin ? adminEquivalentPath(mounted) : null;
  return equivalent ? businessAdminHref(equivalent, options) : buildSurfaceHref('dashboard', path, options);
}

/**
 * Which host owns a `/dashboard/*` path (doc 32 §4.5).
 *
 * `DASHBOARD_ROUTE_HOSTS` is longest-prefix-wins; the bare `/dashboard` landing
 * belongs to the `dashboard` host. Used by `dashboardPathHref` so a Link author
 * never has to know whether their target lives on `dashboard.`, `manage.` or
 * `operations.` — the href resolves host-aware and stays same-origin when the
 * current host already owns the path.
 */
export function resolveDashboardHost(path: string): PlatformPageSurface {
  const normalized = normalizePlatformPath(path);
  let best: { prefix: string; host: PlatformHost } | null = null;
  for (const entry of DASHBOARD_ROUTE_HOSTS) {
    if (normalized === entry.prefix || normalized.startsWith(`${entry.prefix}/`)) {
      if (!best || entry.prefix.length > best.prefix.length) best = entry;
    }
  }
  const host = best?.host ?? 'dashboard';
  return host as PlatformPageSurface;
}

/**
 * Host-aware href for ANY `/dashboard/*` path.
 *
 * Resolves the owning host (`dashboard` / `manage` / `operations`) and delegates
 * to `buildSurfaceHref`, so the result is:
 *   - same-origin relative when the current host already owns the path, and
 *   - an absolute external URL (no RSC prefetch, no CORS) when it does not.
 *
 * Drop-in replacement for raw `/dashboard/...` `Link`/`router.push` targets that
 * may render on a subdomain that does not own the path — e.g. an operations page
 * whose link target lives on `manage.` or `dashboard.` would otherwise 308
 * cross-origin and trip React #418.
 */
export function dashboardPathHref(path = '/', options: HrefOptions = {}) {
  const normalized = normalizePlatformPath(path);
  const inAdmin = currentPlatformHost(options) === 'admin' || (typeof window !== 'undefined' && isBusinessAdminPath(window.location.pathname));
  const equivalent = inAdmin ? adminEquivalentPath(normalized) : null;
  if (equivalent) return businessAdminHref(equivalent, options);
  const owner = resolveDashboardHost(normalized);
  return buildSurfaceHref(owner, normalized, options);
}

export function posHref(branchId: string, path = '/', options: HrefOptions = {}) {
  const normalized = normalizePlatformPath(path);
  const branchPath = `/${encodeURIComponent(branchId)}${normalized === '/' ? '' : normalized}`;
  return buildSurfaceHref('pos', branchPath, options);
}

/**
 * Branch-workspace href (doc 05 §4). The branch id is a route segment:
 * `/dashboard/b/<branchId>/<path>`.
 *
 * `branchId` is a REQUIRED positional argument — the type system makes an
 * un-scoped branch link a compile error, which is the slice-5 invariant. The
 * path is the surface's existing route (sub-path preserved, R3 "same sub-path,
 * new segment"), so `/dashboard/operations/inventory` becomes
 * `/dashboard/b/<branchId>/operations/inventory`.
 *
 * The owning host is resolved from the path, and cross-host behaviour matches
 * `posHref`: on
 * a different production host the segment is carried in an absolute URL (R5);
 * anywhere else the path-based form is emitted, which every host serves.
 */
export function branchHref(branchId: string, path = '/', options: HrefOptions = {}) {
  const normalized = normalizePlatformPath(path);
  const owner = resolveDashboardHost(normalized);
  const branchPath = `/b/${encodeURIComponent(branchId)}${branchCleanPath(normalized)}`;
  return buildSurfaceHref(owner, branchPath, options);
}

/**
 * The branch-workspace path for `branchId` and `path`, without host resolution.
 * `/dashboard/operations/inventory` → `/dashboard/b/<branchId>/operations/inventory`.
 * Used where a raw path is needed (redirect targets, router.push) and by
 * `branchHref`'s callers that must build the path themselves.
 */
export function branchPath(branchId: string, path = '/'): string {
  const normalized = normalizePlatformPath(path);
  return `/dashboard/b/${encodeURIComponent(branchId)}${branchCleanPath(normalized)}`;
}

/**
 * Strip the `/dashboard` mount prefix from a surface route so the branch
 * segment can be inserted: `/dashboard/branch-profile` → `/branch-profile`,
 * `/dashboard/operations/inventory` → `/operations/inventory`. A bare
 * `/dashboard` becomes `/`. Paths without the prefix pass through unchanged
 * (callers may pass either form).
 */
function branchCleanPath(normalized: string): string {
  if (normalized === '/dashboard') return '';
  if (normalized.startsWith('/dashboard/')) return normalized.slice('/dashboard'.length);
  return normalized === '/' ? '' : normalized;
}

export function authHref(path = '/', options: HrefOptions = {}) {
  return buildSurfaceHref('auth', path, options);
}

export function courierHref(path = '/', options: HrefOptions = {}) {
  return buildSurfaceHref('courier', path, options);
}

/** The merchant's own public storefront (doc 32 §4.3) — menus, pay, receipts. */
export function menuHostHref(path = '/', options: HrefOptions = {}) {
  return buildSurfaceHref('m', path, options);
}

export function displayHref(path = '/', options: HrefOptions = {}) {
  return buildSurfaceHref('display', path, options);
}

export function kioskHref(path = '/', options: HrefOptions = {}) {
  return buildSurfaceHref('kiosk', path, options);
}

/** Marketing, docs, legal. */
export function wwwHref(path = '/', options: HrefOptions = {}) {
  return buildSurfaceHref('www', path, options);
}

/**
 * Platform administration (doc 36).
 *
 * Takes a path under the console's mount (`/`, `/payouts`), NOT a mounted path —
 * `buildSurfaceHref` prepends `/admin` itself, so passing `/admin/payouts` here
 * would nest the mount twice, exactly as `branchHref` does if you hand it a full
 * branch path.
 */
export function adminHref(path = '/', options: HrefOptions = {}) {
  return buildSurfaceHref('muhid', path, options);
}

/**
 * Where a signed-in member of the platform team belongs.
 *
 * One function rather than a ternary at each call site, because the two panels and
 * the door that opens them are the only places this mapping is allowed to exist —
 * and because `support` is NOT a sub-path of the console. `/support` is its own
 * hidden mount on the same deployment (blueprint
 * `docs/proposals/platform-plan-2026-10/12-ops-console-and-support.md` §9.1, OD-1),
 * so it cannot go through `buildSurfaceHref('muhid', …)`: that helper prepends the
 * console's `/admin` mount and would emit `/admin/support`.
 *
 * Path-only, not absolute, on purpose. Per-host cookies (doc 32 D4) mean a staff
 * member's session lives on whichever host they signed in on; sending them to an
 * absolute URL is the cross-host hand-off protocol's job, not a redirect's.
 */
export function staffHomePath(role: 'admin' | 'support'): string {
  return role === 'admin' ? '/admin' : '/support';
}

/**
 * The reduced support panel's href helper.
 *
 * `/support` is a second hidden mount on the `muhid` host, NOT a child of `/admin`
 * (blueprint §9.1, OD-1). `buildSurfaceHref('muhid', …)` cannot be reused: it
 * prepends the host's `mountPath` (`/admin`), which would emit `/admin/support` in
 * path-based mode — a route that does not exist — and `adminHref('/support')`
 * would nest the mount twice for the same reason.
 *
 * Unlike a surface `mountPath`, `/support` is NEVER implicit. `muhid.menuraq.com`
 * rewrites its bare root to `/admin`, and `proxy.ts` serves a hidden mount as the
 * app path it already is — so the support panel lives at `/support/...` on every
 * host, including its own. Emitting the short form there (as an earlier version of
 * this helper did) produced `/cases/abc`, a path the router hands to www, where it
 * 404s by design. The mount is part of the address in all three modes.
 *
 *   same host      → `/support/cases/abc`
 *   other live host→ `https://muhid./support/cases/abc`
 *   path-based     → `/support/cases/abc`    (dev, previews, LAN)
 */
export function supportHref(path = '/', options: HrefOptions = {}): string {
  const normalized = normalizePlatformPath(path);
  const cleanPath =
    normalized === '/support'
      ? '/'
      : normalized.startsWith('/support/')
        ? normalized.slice('/support'.length) || '/'
        : normalized;

  const mounted = cleanPath === '/' ? '/support' : `/support${cleanPath}`;

  const here = currentPlatformHost(options);

  if (here === 'muhid') {
    return options.absolute ? new URL(mounted, hostOrigin('muhid')).toString() : mounted;
  }
  if (here && isHostLive('muhid')) {
    return new URL(mounted, hostOrigin('muhid')).toString();
  }
  return mounted;
}

// ─── Origin allowlist ────────────────────────────────────────────────────────

/**
 * Every origin the app may be addressed from.
 *
 * Includes `planned` hosts on purpose. A host must be trusted by the CSRF
 * allowlist and CSP `connect-src` BEFORE it serves, or its very first request
 * fails in a way that looks like a bug: this is exactly how /m sign-in broke in
 * production — `connect-src` still named the retired customer host, so the
 * browser killed the fetch before dispatch and the only symptom was
 * "Network error", with no entry in the network log at all.
 */
export function getAllowedPlatformOrigins(): string[] {
  const origins = new Set<string>([
    'http://localhost:3000',
    'http://localhost:3001',
    'http://127.0.0.1:3000',
    'http://127.0.0.1:3001',
  ]);

  for (const key of Object.keys(PLATFORM_HOSTS) as PlatformHost[]) {
    origins.add(hostOrigin(key));
  }

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || process.env.NEXT_PUBLIC_APP_URL;
  if (siteUrl) {
    for (const entry of siteUrl.split(',')) {
      try {
        origins.add(new URL(entry.trim().replace(/^\[|\]$/g, '')).origin);
      } catch {
        // ignore
      }
    }
  }

  for (const key of [
    'NEXT_PUBLIC_MAIN_ORIGIN',
    'NEXT_PUBLIC_AUTH_ORIGIN',
    'NEXT_PUBLIC_DASHBOARD_ORIGIN',
    'NEXT_PUBLIC_POS_ORIGIN',
    'NEXT_PUBLIC_API_ORIGIN',
  ] as const) {
    const val = process.env[key];
    if (val) {
      try { origins.add(new URL(val).origin); } catch { /* ignore */ }
    }
  }

  return Array.from(origins);
}
